import { filterEmptyQueryParams, getQueriesAsSearchNew } from '@shared/utils/common';
// import { generateRoutePaths } from '@shared/utils/route';

const advisoryManage = {
  rootPath: '/advisory/:id',
  root: (advisoryId: string) => advisoryManage.rootPath.replace(':id', advisoryId),
  details: '/details',
  fees: '/fees',
  media: '/media',
} as const;
export type AdvisoryManagePage = ValueOf<typeof advisoryManage>;

const disposalManageDataRooms = {
  root: '/data-room',
  documents: '/documents',
  marketing: '/marketing',
  publishing: '/publishing',
  users: '/users',
  insights: '/insights',
} as const;

const disposalsManage = {
  rootPath: '/disposals/:id',
  root: (disposalId: string) => disposalsManage.rootPath.replace(':id', disposalId),

  availability: '/availability',
  data_room: disposalManageDataRooms,
  dev_pipeline: '/dev-pipeline',
  fees: '/fees',
  find_matches: '/find-matches',
  // TODO: Refactor insights to be top-level key
  insights: {
    root: '/insights',
    overview: '/overview',
    marketplace_performance: '/marketplace-performance',
  },
  investment: '/investment',
  management: '/management',
  marketing: '/marketing',
  media: '/media',
  reporting: '/reporting',
  reportingWithParams: (disposalId: string, queryParams: Query) => {
    const basePath = `${disposalsManage.root(disposalId)}/reporting`;
    const filteredQueries = filterEmptyQueryParams(queryParams);
    const queryString = getQueriesAsSearchNew(filteredQueries);

    return `${basePath}${queryString}`;
  },
  summary: '/summary',
  transactions: '/transactions',
} as const;
export type DisposalManagePage = ValueOf<typeof disposalsManage>;

const leasesManage = {
  rootPath: '/leases/transactions/:id/manage',
  root: (transactionId: string) => leasesManage.rootPath.replace(':id', transactionId),

  activity: '/activity',
  details: '/details',
  lease_events: '/lease-events',
  management: '/management',
  media: '/media',
  summary: '/summary',
} as const;

const requirementsManage = {
  rootPath: '/requirements/:id',
  root: (requirementId: string) => requirementsManage.rootPath.replace(':id', requirementId),

  availability_schedule: '/shortlist',
  fees: '/fees',
  find_matches: '/search',
  investment_criteria: '/investment-criteria',
  management: '/management',
  matches: '/matches',
  summary: '/summary',
  transactions: '/transactions',
} as const;
export type RequirementManagePage = ValueOf<typeof requirementsManage>;

const salesManage = {
  rootPath: '/sales/transactions/:id/manage',
  root: (transactionId: string) => salesManage.rootPath.replace(':id', transactionId),

  activity: '/activity',
  headline: '/headline',
  media: '/media',
  summary: '/summary',
  tenancy_details: '/tenancy-details',
} as const;

const organisation = {
  advisory: {
    root: '/advisory',
    manage: advisoryManage,
  },
  dashboard: '/dashboard',
  diary: '/diary',
  team: {
    root: '/team',
    members: '/members',
  },

  campaigns: {
    root: '/campaigns',
    single_property: '/single-property',
    multi_property: '/multi-property',
    creating: '/creating',
  },

  crm: {
    root: '/crm',
    contacts: '/contacts',
    companies: '/companies',
  },

  dataManagement: {
    root: '/data-management',
    onboarding: '/onboarding',
    data_imports: '/data-imports',
  },

  disposals: {
    root: '/disposals',
    listDefault: '/disposals/all',
    list: {
      all: '/all',
      investment: '/investment',
      instruction_availability: '/instruction-availability',
      market_availability: '/market-availability',
      dev_pipeline: '/dev-pipeline',
      pitching: '/pitching',
    },
    manage: disposalsManage,
  },

  insights: {
    root: '/insights',
    requirements: '/requirements',
    viewings_old: '/viewings-old',
    viewings: '/viewings',
    sale_transactions: '/sale-transactions',
    letting_transactions: '/letting-transactions',
    disposals: '/disposals',
  },

  markets: {
    root: '/markets',
  },

  properties: {
    root: '/properties',
  },

  requirements: {
    root: '/requirements',
    manage: requirementsManage,
  },

  scheduleInvitees: {
    root: '/schedule-invites',
  },

  settings: {
    root: '/settings',
    features: '/features',
    password: '/password',
    profile: '/profile',
    campaign_emails: '/campaign-emails',
  },

  viewings: '/viewings',

  wipFees: {
    root: '/work-in-progress',
  },

  leases: {
    root: '/leases',
    atlas_data: '/atlas-data',
    radius_rankings: '/radius-rankings',
    transactions: '/transactions',
    lease_events: '/lease-events',
  },
  leases_manage: leasesManage,

  sales: {
    root: '/sales',
    atlas_data: '/atlas-data',
    investment: '/investment',
    occupational: '/occupational',
    radius_rankings: '/radius-rankings',
    transactions: '/transactions',
  },
  sales_manage: salesManage,
};

const styleGuide = {
  root: '/style-guide',
  alerts: '/alerts',
  activity: '/activity',
  badges: '/badges',
  buttons: '/buttons',
  dropdowns: '/dropdowns',
  forms: '/forms',
  icons: '/icons',
  iconsMigration: '/icons-migration',
  layout: '/layout',
  lookups: '/lookups',
  modals: '/modals',
  reports: '/reports',
  skeletonPages: '/skeleton-pages',
  tables: '/tables',
  uploadFiles: '/upload-files',
};

const marketplace = {
  dashboard: '/society/dashboard',
  disposals: '/society/disposals',
  events: '/society/events',
  insights: {
    root: '/society/insights',
    acquisitions: '/acquisitions',
    disposals: '/disposals',
  },
  join: '/marketplace/join',
  members: '/society/members',
  preferences: '/society/preferences',
  requestAccess: '/societies/request-access',
  requirements: '/society/acquisitions',
};

const present = {
  root: '/present',
  disposal: {
    rootPath: '/present/disposal/:id',
    root: (id: string) => present.disposal.rootPath.replace(':id', id),
    configure: '/configure',
  },
};

const ROUTES = {
  initial: '/',
  logOut: '/logout',
  defaultRedirect: '/redirect',

  organisation,

  marketplace,

  present,

  styleGuide,

  upgradeToPro: {
    root: '/upgrade-to-pro',
  },
} as const;

export const routeTitles = {
  [ROUTES.initial]: '',

  [organisation.advisory.root]: 'Advisory',

  [advisoryManage.rootPath]: 'Advisory',
  [`${advisoryManage.rootPath}${advisoryManage.details}`]: 'Details | Advisory',
  [`${advisoryManage.rootPath}${advisoryManage.fees}`]: 'Fees | Advisory',
  [`${advisoryManage.rootPath}${advisoryManage.media}`]: 'Media | Advisory',

  [organisation.campaigns.root]: 'Campaigns',
  [`${organisation.campaigns.root}${organisation.campaigns.creating}`]: 'Creating | Campaigns',
  [`${organisation.campaigns.root}${organisation.campaigns.multi_property}`]: 'Multi-property | Campaigns',
  [`${organisation.campaigns.root}${organisation.campaigns.single_property}`]: 'Single-property | Campaigns',

  [organisation.crm.root]: 'CRM',
  [`${organisation.crm.root}${organisation.crm.companies}`]: 'Companies | CRM',
  [`${organisation.crm.root}${organisation.crm.contacts}`]: 'Contacts | CRM',

  [organisation.dashboard]: 'Home',

  [organisation.dataManagement.root]: 'Data Management',
  [`${organisation.dataManagement.root}${organisation.dataManagement.data_imports}`]: 'Data Imports | Data Management',
  [`${organisation.dataManagement.root}${organisation.dataManagement.onboarding}`]: 'Onboarding | Data Management',

  [organisation.diary]: 'Diary',

  [organisation.disposals.root]: 'Disposals',
  [`${organisation.disposals.root}${organisation.disposals.list.all}`]: 'All | Disposals',
  [`${organisation.disposals.root}${organisation.disposals.list.dev_pipeline}`]: 'Dev Pipeline | Disposals',
  [`${organisation.disposals.root}${organisation.disposals.list.instruction_availability}`]:
    'Our Instructions | Disposals',
  [`${organisation.disposals.root}${organisation.disposals.list.investment}`]: 'Investment | Disposals',
  [`${organisation.disposals.root}${organisation.disposals.list.market_availability}`]:
    'Tracked Availability | Disposals',
  [`${organisation.disposals.root}${organisation.disposals.list.pitching}`]: 'Pitching | Disposals',

  [`${disposalsManage.rootPath}${disposalsManage.availability}`]: 'Availability | Disposal',
  [`${disposalsManage.rootPath}${disposalManageDataRooms.root}`]: 'Data Room | Disposal',
  [`${disposalsManage.rootPath}${disposalManageDataRooms.root}${disposalManageDataRooms.documents}`]:
    'Documents | Data Room | Disposal',
  [`${disposalsManage.rootPath}${disposalManageDataRooms.root}${disposalManageDataRooms.insights}`]:
    'Insights | Data Room | Disposal',
  [`${disposalsManage.rootPath}${disposalManageDataRooms.root}${disposalManageDataRooms.marketing}`]:
    'Marketing | Data Room | Disposal',
  [`${disposalsManage.rootPath}${disposalManageDataRooms.root}${disposalManageDataRooms.publishing}`]:
    'Publishing | Data Room | Disposal',
  [`${disposalsManage.rootPath}${disposalManageDataRooms.root}${disposalManageDataRooms.users}`]:
    'Users | Data Room | Disposal',
  [`${disposalsManage.rootPath}${disposalsManage.dev_pipeline}`]: 'Development Pipeline | Disposal',
  [`${disposalsManage.rootPath}${disposalsManage.fees}`]: 'Fees | Disposal',
  [`${disposalsManage.rootPath}${disposalsManage.find_matches}`]: 'Find Matches | Disposal',
  [`${disposalsManage.rootPath}${disposalsManage.insights.root}`]: 'Insights | Disposal',
  [`${disposalsManage.rootPath}${disposalsManage.insights.root}${disposalsManage.insights.marketplace_performance}`]:
    'Marketplace Performance | Insights | Disposal',
  [`${disposalsManage.rootPath}${disposalsManage.insights.root}${disposalsManage.insights.overview}`]:
    'Overview | Insights | Disposal',
  [`${disposalsManage.rootPath}${disposalsManage.investment}`]: 'Investment | Disposal',
  [`${disposalsManage.rootPath}${disposalsManage.management}`]: 'Management | Disposal',
  [`${disposalsManage.rootPath}${disposalsManage.marketing}`]: 'Marketing | Disposal',
  [`${disposalsManage.rootPath}${disposalsManage.media}`]: 'Media | Disposal',
  [`${disposalsManage.rootPath}${disposalsManage.reporting}`]: 'Interest Schedule | Disposal',
  [`${disposalsManage.rootPath}${disposalsManage.summary}`]: 'Summary | Disposal',
  [`${disposalsManage.rootPath}${disposalsManage.transactions}`]: 'Transactions | Disposal',

  [organisation.insights.root]: 'Agency Insights',
  [`${organisation.insights.root}${organisation.insights.disposals}`]: 'Disposals | Agency Insights',
  [`${organisation.insights.root}${organisation.insights.letting_transactions}`]: 'Leases | Agency Insights',
  [`${organisation.insights.root}${organisation.insights.requirements}`]: 'Requirements | Agency Insights',
  [`${organisation.insights.root}${organisation.insights.sale_transactions}`]: 'Sales | Agency Insights',
  [`${organisation.insights.root}${organisation.insights.viewings}`]: 'Viewings | Agency Insights',
  [`${organisation.insights.root}${organisation.insights.viewings_old}`]: 'Viewings Old | Agency Insights',

  [organisation.markets.root]: 'Leaderboards',

  [organisation.leases.root]: 'Leases',
  [`${organisation.leases.root}${organisation.leases.atlas_data}`]: 'Atlas Data | Leases',
  [`${organisation.leases.root}${organisation.leases.lease_events}`]: 'Lease Events | Leases',
  [`${organisation.leases.root}${organisation.leases.radius_rankings}`]: 'Radius Rankings | Leases',
  [`${organisation.leases.root}${organisation.leases.transactions}`]: 'All | Leases',

  [`${leasesManage.rootPath}`]: 'Lease Transaction',
  [`${leasesManage.rootPath}${leasesManage.activity}`]: 'Activity | Lease Transaction',
  [`${leasesManage.rootPath}${leasesManage.details}`]: 'Transaction Details | Lease Transaction',
  [`${leasesManage.rootPath}${leasesManage.lease_events}`]: 'Lease Events | Lease Transaction',
  [`${leasesManage.rootPath}${leasesManage.management}`]: 'Management | Lease Transaction',
  [`${leasesManage.rootPath}${leasesManage.summary}`]: 'Summary | Lease Transaction',

  [present.root]: 'Present',
  [`${present.disposal.rootPath}`]: 'Disposal | Present',
  [`${present.disposal.rootPath}${present.disposal.configure}`]: 'Configure | Disposal | Present',

  [organisation.properties.root]: 'Properties',

  [organisation.requirements.root]: 'Requirements',

  [requirementsManage.rootPath]: 'Requirement',
  [`${requirementsManage.rootPath}${requirementsManage.availability_schedule}`]: 'Availability Schedule | Requirement',
  [`${requirementsManage.rootPath}${requirementsManage.fees}`]: 'Fees | Requirement',
  [`${requirementsManage.rootPath}${requirementsManage.find_matches}`]: 'Find Matches | Requirement',
  [`${requirementsManage.rootPath}${requirementsManage.investment_criteria}`]: 'Investment Criteria | Requirement',
  [`${requirementsManage.rootPath}${requirementsManage.management}`]: 'Management | Requirement',
  [`${requirementsManage.rootPath}${requirementsManage.matches}`]: 'Matches | Requirement',
  [`${requirementsManage.rootPath}${requirementsManage.summary}`]: 'Summary | Requirement',
  [`${requirementsManage.rootPath}${requirementsManage.transactions}`]: 'Transactions | Requirement',

  [organisation.sales.root]: 'Sales',
  [`${organisation.sales.root}${organisation.sales.atlas_data}`]: 'Atlas Data | Sales',
  [`${organisation.sales.root}${organisation.sales.investment}`]: 'Investment | Sales',
  [`${organisation.sales.root}${organisation.sales.occupational}`]: 'Occupational | Sales',
  [`${organisation.sales.root}${organisation.sales.radius_rankings}`]: 'Radius Rankings | Sales',
  [`${organisation.sales.root}${organisation.sales.transactions}`]: 'All | Sales',

  [`${salesManage.rootPath}`]: 'Sale Transaction',
  [`${salesManage.rootPath}${salesManage.summary}`]: 'Summary | Sale Transaction',
  [`${salesManage.rootPath}${salesManage.activity}`]: 'Activity | Lease Transaction',

  [organisation.scheduleInvitees.root]: 'Schedule Invites',

  [organisation.settings.root]: 'Account Settings',
  [`${organisation.settings.root}${organisation.settings.campaign_emails}`]: 'Email Templates | Account Settings',
  [`${organisation.settings.root}${organisation.settings.features}`]: 'Features | Account Settings',
  [`${organisation.settings.root}${organisation.settings.password}`]: 'Password | Account Settings',
  [`${organisation.settings.root}${organisation.settings.profile}`]: 'Profile | Account Settings',

  [marketplace.dashboard]: 'Marketplace Dashboard',
  [marketplace.disposals]: 'Marketplace Disposals',
  [marketplace.events]: 'Marketplace Events',
  [marketplace.insights.root]: 'Marketplace Insights',
  [`${marketplace.insights.root}${marketplace.insights.acquisitions}`]: 'Acquisitions | Marketplace Insights',
  [`${marketplace.insights.root}${marketplace.insights.disposals}`]: 'Disposals | Marketplace Insights',
  [marketplace.members]: 'Marketplace Members',
  [marketplace.requirements]: 'Marketplace Requirements',
  [marketplace.preferences]: 'Marketplace Preferences',

  [styleGuide.root]: 'Style Guide',
  [`${styleGuide.root}${styleGuide.activity}`]: 'Activity | Style Guide',
  [`${styleGuide.root}${styleGuide.alerts}`]: 'Alerts | Style Guide',
  [`${styleGuide.root}${styleGuide.badges}`]: 'Badges | Style Guide',
  [`${styleGuide.root}${styleGuide.buttons}`]: 'Buttons | Style Guide',
  [`${styleGuide.root}${styleGuide.dropdowns}`]: 'Dropdowns | Style Guide',
  [`${styleGuide.root}${styleGuide.forms}`]: 'Forms | Style Guide',
  [`${styleGuide.root}${styleGuide.icons}`]: 'Icons | Style Guide',
  [`${styleGuide.root}${styleGuide.iconsMigration}`]: 'Icons Migration | Style Guide',
  [`${styleGuide.root}${styleGuide.layout}`]: 'Layout | Style Guide',
  [`${styleGuide.root}${styleGuide.lookups}`]: 'Lookups | Style Guide',
  [`${styleGuide.root}${styleGuide.modals}`]: 'Modals | Style Guide',
  [`${styleGuide.root}${styleGuide.reports}`]: 'Reports | Style Guide',
  [`${styleGuide.root}${styleGuide.skeletonPages}`]: 'Skeleton Pages | Style Guide',
  [`${styleGuide.root}${styleGuide.tables}`]: 'Tables | Style Guide',
  [`${styleGuide.root}${styleGuide.uploadFiles}`]: 'Upload Files | Style Guide',

  [organisation.team.root]: 'Team',
  [`${organisation.team.root}${organisation.team.members}`]: 'Team',

  [organisation.viewings]: 'Viewings',

  [organisation.wipFees.root]: 'Work in Progress',
} as const;

// TODO: Used for outputting object of all paths, to aid with maintaining above (find missing keys)
// const list = generateRoutePaths(ROUTES).sort();
// console.log(list.reduce((result, value) => {
//   result[value] = '';
//   return result;
// }, {} as Record<string, string>));

export default ROUTES;
